import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Check, ChevronDown, Loader2, Search } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { FORM_CONTROL_OPEN_CLASS } from '@/components/ui/form-control-styles'
import { cn } from '@/lib/utils'
import { useDebounce } from '@/hooks/useDebounce'
import { PICKER_LIMIT } from '@/lib/listFetch'
import type { ApiList } from '@/types'

export type EntityPickerFetchParams = {
  search: string
  limit: number
  page: number
}

interface EntityPickerProps<T> {
  // Server-side search fetcher. Receives the search term + capped limit + page.
  // MUST hit a paginated endpoint with `search` + `limit` + `page` (never fetch-all).
  fetcher: (params: EntityPickerFetchParams) => Promise<ApiList<T>>
  // Stable query key prefix for caching.
  queryKey: string
  getOptionLabel: (item: T) => string
  getOptionValue: (item: T) => string
  /** Optional richer option row (e.g. name + Prospect badge). */
  renderOption?: (item: T) => ReactNode
  /** Optional selected-value display; `item` is undefined until the list is fetched. */
  renderValue?: (label: string, item: T | undefined) => ReactNode
  value?: string | null
  onChange: (value: string, item: T) => void
  placeholder?: string
  // Fallback label shown when value is set but the item isn't in the fetched
  // list yet (e.g. edit forms before the dropdown is first opened).
  initialLabel?: string
  limit?: number
  className?: string
  // Search-style trigger (leading magnifier, no chevron) — Figma form pickers.
  variant?: 'default' | 'search'
  // Read-only: shows the selected label but blocks opening the dropdown. Used
  // when the value is fixed by context (e.g. the job-driven payment flow).
  disabled?: boolean
  // Load the first page as soon as the picker mounts instead of waiting for
  // the dropdown to open. Search and subsequent pages remain demand-driven.
  preload?: boolean
  // Override the Radix portal host when the picker lives inside a scroll-locked
  // dialog/drawer so the dropdown remains fully interactive.
  portalContainer?: HTMLElement | null
}

// Debounced, server-side searchable single-select. Pages lazily: the first
// request is capped at PICKER_LIMIT, then further pages load when the user
// scrolls near the end of the option list.
export function EntityPicker<T>({
  fetcher,
  queryKey,
  getOptionLabel,
  getOptionValue,
  renderOption,
  renderValue,
  value,
  onChange,
  placeholder,
  initialLabel,
  limit = PICKER_LIMIT,
  className,
  variant = 'default',
  disabled = false,
  preload = false,
  portalContainer,
}: EntityPickerProps<T>) {
  const { t } = useTranslation('shell')
  const resolvedPlaceholder = placeholder ?? t('picker.placeholder')
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const debounced = useDebounce(term, 300)
  const cappedLimit = Math.min(limit, PICKER_LIMIT)
  const listRef = useRef<HTMLDivElement>(null)

  const {
    data,
    isFetching,
    isFetchingNextPage,
    isError,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: [queryKey, 'picker', debounced, cappedLimit],
    queryFn: ({ pageParam }) =>
      fetcher({ search: debounced, limit: cappedLimit, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: last => {
      const meta = last.metadata
      if (!meta) return undefined
      return meta.current_page < meta.last_page
        ? meta.current_page + 1
        : undefined
    },
    enabled: open || preload,
  })

  const items = data?.pages.flatMap(page => page.data) ?? []
  const selectedItem = items.find(i => getOptionValue(i) === value)
  const selectedLabel = selectedItem
    ? getOptionLabel(selectedItem)
    : value
      ? initialLabel
      : undefined
  const searching = isFetching && !isFetchingNextPage
  const loadNextPage = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return
    void fetchNextPage()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  useEffect(() => {
    const list = listRef.current
    if (!list || !hasNextPage || !open || isFetchingNextPage) return

    // Keep loading until the menu becomes scrollable so users never need a
    // manual "load more" action just to continue browsing the list.
    if (list.scrollHeight <= list.clientHeight + 16) {
      loadNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, items.length, loadNextPage, open])

  return (
    <DropdownMenu
      open={open}
      onOpenChange={next => !disabled && setOpen(next)}
      modal={false}
    >
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          variant='outline'
          disabled={disabled}
          data-has-value={selectedLabel ? 'true' : 'false'}
          className={cn(
            'w-full font-normal',
            variant === 'search' ? 'justify-start gap-2' : 'justify-between',
            disabled &&
              'disabled:cursor-default disabled:bg-[#f4f5f8] disabled:opacity-100',
            className,
            open && FORM_CONTROL_OPEN_CLASS
          )}
        >
          {variant === 'search' && (
            <Search className='size-4 shrink-0 text-[#6c7285]' />
          )}
          <span
            className={cn(
              'flex min-w-0 items-center gap-1 font-normal',
              !selectedLabel && 'text-[#6c7285]'
            )}
          >
            {selectedLabel && renderValue ? (
              renderValue(selectedLabel, selectedItem)
            ) : (
              <span className='min-w-0 truncate'>
                {selectedLabel ?? resolvedPlaceholder}
              </span>
            )}
          </span>
          {variant !== 'search' && (
            <ChevronDown className='h-4 w-4 shrink-0 opacity-50' />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        container={portalContainer}
        className='w-(--radix-dropdown-menu-trigger-width) border-[#e7e9ef] p-0'
      >
        <div className='relative border-b border-[#e7e9ef]'>
          <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6c7285]' />
          <input
            autoFocus
            value={term}
            onChange={e => setTerm(e.target.value)}
            placeholder={t('picker.searchPlaceholder')}
            className='h-9 w-full bg-transparent px-9 text-sm font-normal leading-[1.4] text-[#171a20] outline-none placeholder:text-[#6c7285]'
          />
          {searching && (
            <Loader2 className='absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-[#6c7285]' />
          )}
        </div>
        <div
          ref={listRef}
          className='max-h-64 overflow-auto p-1'
          onScroll={event => {
            const list = event.currentTarget
            const remaining =
              list.scrollHeight - list.scrollTop - list.clientHeight
            if (remaining <= 40) loadNextPage()
          }}
        >
          {isError && (
            <p className='px-3 py-2 text-sm font-normal leading-[1.4] text-destructive'>
              {t('picker.unableToLoad')}
            </p>
          )}
          {!isError && items.length === 0 && !searching && (
            <p className='px-3 py-2 text-sm font-normal leading-[1.4] text-[#6c7285]'>
              {t('picker.noResults')}
            </p>
          )}
          {items.map(item => {
            const v = getOptionValue(item)
            return (
              <button
                key={v}
                type='button'
                onClick={() => {
                  onChange(v, item)
                  setOpen(false)
                }}
                className='flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm font-normal leading-[1.4] text-[#171a20] hover:bg-[#f4f5f8]'
              >
                <span className='min-w-0 flex-1'>
                  {renderOption ? renderOption(item) : getOptionLabel(item)}
                </span>
                {v === value && <Check className='h-4 w-4 shrink-0' />}
              </button>
            )
          })}
          {isFetchingNextPage && (
            <div className='flex items-center justify-center px-3 py-2 text-[#6c7285]'>
              <Loader2 className='h-4 w-4 animate-spin' />
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default EntityPicker
